import {
  ATTENTION_KINDS,
  BLOCKS_RELEASE,
  LIVE_STATUSES,
  type AttentionKind,
  type AttentionReport,
  type AttentionRow,
  type Evidence,
  type Gate,
  type GateStatus,
  type HoldPointReport,
  type InspectionItem,
  type InspectionResult,
  type ProjectDetail,
  type ProjectInspectionDetail,
  type ProjectInspectionRow,
  type ProjectRow,
  type ProjectVocabulary,
  type Quality,
} from "../domain/projects";
import { TODAY } from "./clock";

/*
 * A sample capital programme at Madinah, shaped to exercise every state the
 * platform can report rather than to look tidy: work stopped by a hold point
 * nobody has inspected, a hold point that passed and awaits only a signature,
 * one that failed and cannot be released at all, a gate approved with its
 * evidence never supplied, a project with no baseline, one overdue and
 * rebaselined, one never inspected, and one finished.
 *
 * The seven exist because the "needs attention" screen has nine lists and the
 * platform's own note insists they are nine different failures with nine
 * different fixes. A sample in which most of them are empty would let a screen
 * that renders them all identically look correct.
 *
 * Everything here is labelled SAMPLE DATA in the UI.
 */

function d(daysFromToday: number, hour = 6): string {
  const x = new Date(TODAY);
  x.setUTCDate(x.getUTCDate() + daysFromToday);
  x.setUTCHours(hour, 0, 0, 0);
  return x.toISOString();
}
function day(daysFromToday: number): string {
  return d(daysFromToday).slice(0, 10);
}

const SITE = "site_oema";

/* ---- the packs' vocabulary ------------------------------------------------------ */

/**
 * What the sample "airports" pack declares. Nothing in the UI hard-codes a
 * gate model, so this is where the sample's one comes from — the same place
 * the platform reads a real deployment's.
 */
export const PROJECT_VOCABULARY: ProjectVocabulary = {
  gateSets: [
    {
      key: "capital_gates",
      version: "1.2",
      name: "Capital works stage gates",
      pack: "airports",
      description: "Five stage gates from concept to handover, as the aerodrome capital procedure sets them.",
      gates: [
        { key: "concept", name: "Concept approved", requires: ["business_case", "safety_screening"] },
        { key: "design", name: "Design approved", requires: ["design_certificate", "design_review_minutes"] },
        { key: "tender", name: "Tender awarded", requires: ["tender_report"] },
        {
          key: "construction_start",
          name: "Construction may start",
          requires: ["method_statement", "safety_case", "permit_to_work"],
        },
        { key: "handover", name: "Handover accepted", requires: ["as_built_drawings", "o_and_m_manuals"] },
      ],
    },
  ],
  disciplines: [
    { key: "civil", name: "Civil", pack: "airports" },
    { key: "geotechnical", name: "Geotechnical", pack: "airports" },
    { key: "electrical", name: "Electrical", pack: "airports" },
    { key: "mechanical", name: "Mechanical", pack: "airports" },
    { key: "quality", name: "Quality assurance", pack: "airports" },
  ],
  categories: [
    {
      key: "airfield_pavement",
      name: "Airfield pavement",
      pack: "airports",
      domain: "airside",
      defaultGateSet: "capital_gates",
    },
    {
      key: "airfield_lighting",
      name: "Airfield lighting",
      pack: "airports",
      domain: "airside",
      defaultGateSet: "capital_gates",
    },
    { key: "buildings", name: "Buildings", pack: "airports", domain: "terminal", defaultGateSet: "capital_gates" },
    { key: "landside", name: "Landside", pack: "airports", domain: "landside", defaultGateSet: "capital_gates" },
    { key: "utilities", name: "Utilities", pack: "airports", domain: "estate", defaultGateSet: "capital_gates" },
  ],
  issues: [],
};

const GATE_NAMES = new Map(PROJECT_VOCABULARY.gateSets[0].gates.map((g) => [g.key, g]));

/* ---- builders ------------------------------------------------------------------- */

/**
 * `evidence_state` from gates.py, reproduced so the sample shows the same five
 * states the platform distinguishes — in particular `decided_without_evidence`,
 * which is the one a screen is most tempted to render as plain "approved".
 */
export function evidenceOf(status: GateStatus, required: string[], supplied: string[]): Evidence {
  const missing = required.filter((k) => !supplied.includes(k));
  const decided = status === "approved" || status === "rejected" || status === "waived";
  if (required.length === 0)
    return {
      state: "none_required",
      required: [],
      supplied,
      missing: [],
      why: "the gate set asks this gate for no evidence, so nothing about its evidence can be said either way",
    };
  if (!decided)
    return {
      state: missing.length ? "incomplete" : "complete",
      required,
      supplied,
      missing,
      why: missing.length
        ? `${missing.length} of ${required.length} required item(s) have not been supplied: ${missing.join(", ")}`
        : "every required item has been supplied",
    };
  if (missing.length)
    return {
      state: "decided_without_evidence",
      required,
      supplied,
      missing,
      why: `this gate was ${status} with ${missing.length} of ${required.length} required item(s) never supplied: ${missing.join(
        ", ",
      )}. That is recorded here for the life of the project`,
    };
  return {
    state: "decided",
    required,
    supplied,
    missing: [],
    why: `${status} with all evidence present`,
  };
}

type GateSeed = {
  key: string;
  status: GateStatus;
  supplied?: string[];
  submittedAt?: string;
  decidedAt?: string;
  decidedByName?: string;
  decisionNote?: string;
  dueDate?: string;
};

function gatesOf(projectId: string, seeds: GateSeed[]): Gate[] {
  return seeds.map((g, i) => {
    const declared = GATE_NAMES.get(g.key);
    const required = declared?.requires ?? [];
    return {
      id: `${projectId}-gate-${g.key}`,
      seq: i + 1,
      key: g.key,
      name: declared?.name ?? g.key,
      status: g.status,
      evidence: evidenceOf(g.status, required, g.supplied ?? []),
      dueDate: g.dueDate,
      submittedAt: g.submittedAt,
      decidedAt: g.decidedAt,
      decidedByName: g.decidedByName,
      decisionNote: g.decisionNote,
    };
  });
}

/** `quality_of` from inspections.py. `complete` means answered, never passed. */
export function qualityOf(items: readonly InspectionItem[]): Quality {
  const count = (r: InspectionItem["result"]) => items.filter((i) => i.result === r).length;
  const total = items.length;
  const notChecked = count("not_checked");
  const passed = count("pass");
  const failed = count("fail");
  const notApplicable = count("not_applicable");
  const why =
    total === 0
      ? "this inspection has no items. That is not the same as an inspection on which nothing was found wrong"
      : notChecked === total
        ? `none of the ${total} items has been checked`
        : notChecked
          ? `${notChecked} of ${total} items have not been checked. An unchecked item is not a pass`
          : `all ${total} items were answered: ${passed} pass, ${failed} fail, ${notApplicable} not applicable`;
  return {
    state: total === 0 ? "no_items" : notChecked === total ? "not_started" : notChecked ? "incomplete" : "complete",
    items: total,
    passed,
    failed,
    notApplicable,
    notChecked,
    why,
  };
}

/** `_holding_because` from inspections.py, word for word. */
export function holdingBecause(result: InspectionResult | undefined, notChecked: number, items: number): string {
  if (!result) return "nothing has been inspected yet, so there is nothing to release against";
  if (BLOCKS_RELEASE.includes(result))
    return "the inspection failed; it is cleared by re-inspecting the work, not by releasing this point";
  if (notChecked) return `${notChecked} of ${items} items are unchecked, so the sheet is not finished`;
  return `inspected '${result}' and awaiting a release signature; passing the checks is not the same as being released`;
}

function sheet(prompts: [prompt: string, result: InspectionItem["result"], note?: string][]): InspectionItem[] {
  return prompts.map(([prompt, result, note], i) => ({
    seq: i + 1,
    itemKey: `item_${i + 1}`,
    prompt,
    result,
    note,
  }));
}

/* ---- the inspections ------------------------------------------------------------ */

export const SHEETS: ProjectInspectionDetail[] = [
  /* Nothing inspected: the hold point that is stopping Runway 17/35. */
  {
    id: "pi-0001-003",
    reference: "PRJ-2026-0001/003",
    title: "Subbase level survey before asphalt overlay",
    kind: "hold",
    discipline: "geotechnical",
    checklistKey: "subbase_level_survey",
    location: "RWY 17/35 CH 1200–1500",
    scheduledFor: d(-2, 5),
    projectId: "prj-0001",
    projectCode: "PRJ-2026-0001",
    projectName: "Runway 17/35 pavement rehabilitation — Phase 2",
    siteId: SITE,
    defects: 0,
    stoppingWork: true,
    createdAt: d(-9),
    sheet: sheet([
      ["Levels within ±10 mm of design at 20 m chainage", "not_checked"],
      ["Compaction test results attached (min 98% MDD)", "not_checked"],
      ["Subbase free of standing water and loose material", "not_checked"],
      ["Edge restraint set to line", "not_checked"],
      ["Survey control points verified against station RS-04", "not_checked"],
      ["Geotextile lap ≥ 300 mm and undamaged", "not_checked"],
    ]),
    quality: { state: "not_started", items: 6, passed: 0, failed: 0, notApplicable: 0, notChecked: 6 },
  },
  /* Inspected, passed, sheet finished: only the signature is missing. */
  {
    id: "pi-0002-002",
    reference: "PRJ-2026-0002/002",
    title: "Concrete pour — kerb line CH 42+00",
    kind: "hold",
    discipline: "civil",
    checklistKey: "concrete_pour_release",
    location: "TWY B, CH 42+00",
    scheduledFor: d(-1, 5),
    performedAt: d(-1, 9),
    performedByName: "Hani Al-Otaibi",
    witnessedBy: "Dar Al-Handasah (RE)",
    result: "pass_with_observations",
    notes: "Slump 90 mm against 75±25. Two cubes taken for 7-day and 28-day tests.",
    projectId: "prj-0002",
    projectCode: "PRJ-2026-0002",
    projectName: "Taxiway B widening to Code E",
    siteId: SITE,
    defects: 0,
    stoppingWork: true,
    createdAt: d(-4),
    sheet: sheet([
      ["Formwork line and level checked", "pass"],
      ["Reinforcement cover verified (50 mm)", "pass"],
      ["Concrete delivery ticket matches specified mix", "pass"],
      ["Slump test within tolerance", "pass", "90 mm — within 75±25"],
      ["Test cubes taken and labelled", "pass"],
      ["Weather within pouring limits", "pass"],
      ["Curing method agreed and materials on site", "pass"],
      ["Night pour lighting assessment", "not_applicable", "Daytime pour"],
    ]),
    quality: { state: "complete", items: 8, passed: 7, failed: 0, notApplicable: 1, notChecked: 0 },
  },
  /* Failed: cleared by re-inspecting, never by a signature. */
  {
    id: "pi-0007-001",
    reference: "PRJ-2026-0007/001",
    title: "Culvert invert level and bedding before backfill",
    kind: "hold",
    discipline: "civil",
    location: "North airside, culvert C-12",
    scheduledFor: d(-3, 5),
    performedAt: d(-3, 11),
    performedByName: "Hani Al-Otaibi",
    result: "fail",
    notes: "Invert 40 mm high against design; bedding not to specified grading.",
    projectId: "prj-0007",
    projectCode: "PRJ-2026-0007",
    projectName: "Stormwater culvert repair — north airside",
    siteId: SITE,
    defects: 1,
    stoppingWork: true,
    createdAt: d(-6),
    sheet: [
      ...sheet([
        ["Trench dewatered", "pass"],
        ["Bedding material to specified grading", "fail", "Oversize material through the bedding layer"],
        ["Invert level within ±15 mm of design", "fail", "40 mm high at both ends"],
        ["Pipe joints seated and sealed", "pass"],
        ["Backfill material approved", "pass"],
      ]),
    ].map((i) => (i.seq === 3 ? { ...i, defectId: "def-c12-invert", defectStatus: "open", defectSeverity: 2 } : i)),
    quality: { state: "complete", items: 5, passed: 3, failed: 2, notApplicable: 0, notChecked: 0 },
  },
  /* A sheet somebody started and stopped, with a result already recorded. */
  {
    id: "pi-0004-006",
    reference: "PRJ-2026-0004/006",
    title: "Baggage handling system — commissioning witness, line 3",
    kind: "witness",
    discipline: "mechanical",
    location: "T1 baggage hall",
    scheduledFor: d(-11, 5),
    performedAt: d(-11, 14),
    performedByName: "Nada Al-Zahrani",
    result: "conditional",
    notes: "Stopped at item 8 when the tilt-tray drive tripped. Remainder not witnessed.",
    projectId: "prj-0004",
    projectCode: "PRJ-2026-0004",
    projectName: "Terminal 1 baggage hall extension",
    siteId: SITE,
    defects: 0,
    stoppingWork: false,
    createdAt: d(-14),
    sheet: sheet([
      ["Emergency stops functional on all access points", "pass"],
      ["Belt speed within specification", "pass"],
      ["Weight capture accurate to ±0.2 kg", "pass"],
      ["Barcode read rate ≥ 98% over 200 bags", "pass"],
      ["Reject lane diverts correctly", "pass"],
      ["Fire alarm interface stops the line", "pass"],
      ["Manual encoding station operates", "pass"],
      ["Tilt-tray drive under full load", "fail", "Drive tripped on overload after 6 minutes"],
      ["Sortation accuracy over 500 bags", "not_checked"],
      ["Handover to operations training complete", "not_checked"],
    ]),
    quality: { state: "incomplete", items: 10, passed: 7, failed: 1, notApplicable: 0, notChecked: 2 },
  },
  /* Ordinary surveillance on the runway project — recorded, no hold. */
  {
    id: "pi-0001-002",
    reference: "PRJ-2026-0001/002",
    title: "Asphalt plant surveillance — mix temperature and grading",
    kind: "surveillance",
    discipline: "quality",
    location: "Batching plant, Wadi Al-Aqiq",
    performedAt: d(-5, 8),
    performedByName: "Nada Al-Zahrani",
    result: "pass",
    projectId: "prj-0001",
    projectCode: "PRJ-2026-0001",
    projectName: "Runway 17/35 pavement rehabilitation — Phase 2",
    siteId: SITE,
    defects: 0,
    stoppingWork: false,
    createdAt: d(-5),
    sheet: sheet([
      ["Mix temperature at discharge 150–165 °C", "pass"],
      ["Grading within job-mix tolerance", "pass"],
      ["Binder content within ±0.3%", "pass"],
    ]),
    quality: { state: "complete", items: 3, passed: 3, failed: 0, notApplicable: 0, notChecked: 0 },
  },
  /* A hold point that was released — what the finished state looks like. */
  {
    id: "pi-0006-004",
    reference: "PRJ-2026-0006/004",
    title: "Final surface regularity and texture depth",
    kind: "hold",
    discipline: "civil",
    location: "Apron 3, stands 31–36",
    performedAt: d(-45, 7),
    performedByName: "Hani Al-Otaibi",
    result: "pass",
    releasedAt: d(-44, 9),
    releasedByName: "Mona Al-Harbi",
    notes: "Texture depth 1.1 mm mean. Released for line marking.",
    projectId: "prj-0006",
    projectCode: "PRJ-2026-0006",
    projectName: "Apron 3 resurfacing",
    siteId: SITE,
    defects: 0,
    stoppingWork: false,
    createdAt: d(-50),
    sheet: sheet([
      ["Surface regularity within 3 mm under a 3 m straightedge", "pass"],
      ["Texture depth ≥ 1.0 mm (sand patch)", "pass"],
      ["Falls to drainage as designed", "pass"],
      ["No segregation or bleeding visible", "pass"],
    ]),
    quality: { state: "complete", items: 4, passed: 4, failed: 0, notApplicable: 0, notChecked: 0 },
  },
];

/* ---- the portfolio -------------------------------------------------------------- */

type ProjectSeed = Omit<
  ProjectDetail,
  "gates" | "gatesApproved" | "holding" | "workStopped" | "gateList" | "gateSummary" | "inspections" | "holdPoints"
> & { gateSeeds: GateSeed[] };

function money(budget?: number, committed?: number, spent?: number): ProjectDetail["money"] {
  const declared = [budget, committed, spent].some((v) => v != null);
  return {
    currency: "SAR",
    budgetMinor: budget,
    committedMinor: committed,
    spentMinor: spent,
    state: declared ? "declared" : "not_declared",
    note: "whole minor units of the stated currency. The core reports these and computes nothing from them: it is not told whether committed includes spent, so any variance it produced would be a guess presented as a figure",
  };
}

const SEEDS: ProjectSeed[] = [
  {
    id: "prj-0001",
    code: "PRJ-2026-0001",
    name: "Runway 17/35 pavement rehabilitation — Phase 2",
    description:
      "Mill and overlay of the northern 1,500 m of RWY 17/35, with subbase reconstruction between CH 1200 and CH 1500.",
    category: "airfield_pavement",
    domain: "airside",
    status: "construction",
    siteId: SITE,
    siteCode: "OEMA",
    contractor: "Al-Rajhi Contracting",
    consultant: "Dar Al-Handasah",
    ownerName: "Mona Al-Harbi",
    gateSet: { key: "capital_gates", version: "1.2", pack: "airports" },
    schedule: {
      state: "slipped",
      why: "the current end date is 46 day(s) later than the baseline of 2026-11-30",
      baselineStart: "2026-03-01",
      baselineEnd: "2026-11-30",
      startDate: "2026-03-08",
      endDate: "2027-01-15",
      endSlipDays: 46,
      startSlipDays: 7,
      rebaselined: 0,
    },
    money: money(8_400_000_00, 7_900_000_00, 4_120_000_00),
    rebaselines: [],
    links: [
      {
        entityKind: "zone",
        entityId: "z-rwy-17-35",
        relation: "affects",
        entityRef: "RWY-17-35",
        entityName: "Runway 17/35",
      },
      {
        entityKind: "document",
        entityId: "doc-fric-proc",
        relation: "governed_by",
        entityRef: "SLT-PR-014",
        entityName: "Runway friction survey procedure",
      },
    ],
    createdAt: d(-190),
    updatedAt: d(-2),
    gateSeeds: [
      {
        key: "concept",
        status: "approved",
        supplied: ["business_case", "safety_screening"],
        submittedAt: d(-186),
        decidedAt: d(-180),
        decidedByName: "Mona Al-Harbi",
      },
      {
        /* Approved with the design certificate never supplied. The platform
           records this rather than refusing it, and it stays visible here. */
        key: "design",
        status: "approved",
        supplied: ["design_review_minutes"],
        submittedAt: d(-150),
        decidedAt: d(-141),
        decidedByName: "Mona Al-Harbi",
        decisionNote: "Approved to hold the programme; certificate to follow from the designer.",
      },
      {
        key: "tender",
        status: "approved",
        supplied: ["tender_report"],
        submittedAt: d(-120),
        decidedAt: d(-112),
        decidedByName: "Faisal Al-Ghamdi",
      },
      {
        key: "construction_start",
        status: "submitted",
        supplied: ["safety_case", "permit_to_work"],
        submittedAt: d(-8),
        dueDate: day(2),
      },
      { key: "handover", status: "pending" },
    ],
  },
  {
    id: "prj-0002",
    code: "PRJ-2026-0002",
    name: "Taxiway B widening to Code E",
    description: "Widening of TWY B between CH 38+00 and CH 46+00 to 23 m with shoulders, including kerb and drainage.",
    category: "airfield_pavement",
    domain: "airside",
    status: "construction",
    siteId: SITE,
    siteCode: "OEMA",
    contractor: "Nesma & Partners",
    consultant: "Dar Al-Handasah",
    ownerName: "Faisal Al-Ghamdi",
    gateSet: { key: "capital_gates", version: "1.2", pack: "airports" },
    schedule: {
      state: "on_baseline",
      why: "the current end date still matches the baseline of 2027-02-28",
      baselineStart: "2026-06-01",
      baselineEnd: "2027-02-28",
      startDate: "2026-06-01",
      endDate: "2027-02-28",
      endSlipDays: 0,
      startSlipDays: 0,
      rebaselined: 0,
    },
    money: money(3_150_000_00, 3_010_000_00, 620_000_00),
    rebaselines: [],
    links: [
      { entityKind: "zone", entityId: "z-twy-b", relation: "affects", entityRef: "TWY-B", entityName: "Taxiway B" },
    ],
    createdAt: d(-120),
    updatedAt: d(-1),
    gateSeeds: [
      {
        key: "concept",
        status: "approved",
        supplied: ["business_case", "safety_screening"],
        decidedAt: d(-115),
        decidedByName: "Mona Al-Harbi",
      },
      {
        key: "design",
        status: "approved",
        supplied: ["design_certificate", "design_review_minutes"],
        decidedAt: d(-96),
        decidedByName: "Mona Al-Harbi",
      },
      {
        key: "tender",
        status: "approved",
        supplied: ["tender_report"],
        decidedAt: d(-80),
        decidedByName: "Faisal Al-Ghamdi",
      },
      {
        key: "construction_start",
        status: "approved",
        supplied: ["method_statement", "safety_case", "permit_to_work"],
        decidedAt: d(-70),
        decidedByName: "Mona Al-Harbi",
      },
      { key: "handover", status: "pending" },
    ],
  },
  {
    id: "prj-0003",
    code: "PRJ-2026-0003",
    name: "AGL constant-current regulator replacement — Vault 2",
    description: "Replacement of six CCRs serving the runway edge and centreline circuits, with new monitoring.",
    category: "airfield_lighting",
    domain: "airside",
    status: "design",
    siteId: SITE,
    siteCode: "OEMA",
    consultant: "ADPi",
    ownerName: "Faisal Al-Ghamdi",
    gateSet: { key: "capital_gates", version: "1.2", pack: "airports" },
    /* A current end date and no baseline. Whether it is late is unanswerable,
       and the platform says so rather than reporting a slip of zero. */
    schedule: {
      state: "no_baseline",
      why: "this project has a current end date but no baseline, so whether it is late is unanswerable rather than 'no'",
      startDate: "2026-08-01",
      endDate: "2027-06-30",
      rebaselined: 0,
    },
    money: money(),
    rebaselines: [],
    links: [],
    createdAt: d(-40),
    updatedAt: d(-12),
    gateSeeds: [
      {
        key: "concept",
        status: "approved",
        supplied: ["business_case", "safety_screening"],
        decidedAt: d(-30),
        decidedByName: "Mona Al-Harbi",
      },
      {
        key: "design",
        status: "submitted",
        supplied: ["design_review_minutes"],
        submittedAt: d(-19),
        dueDate: day(-4),
      },
      { key: "tender", status: "pending" },
    ],
  },
  {
    id: "prj-0004",
    code: "PRJ-2026-0004",
    name: "Terminal 1 baggage hall extension",
    description: "Extension of the T1 baggage hall by 2,400 m² with a third make-up carousel and screening line.",
    category: "buildings",
    domain: "terminal",
    status: "commissioning",
    siteId: SITE,
    siteCode: "OEMA",
    contractor: "Almabani General Contractors",
    consultant: "ADPi",
    ownerName: "Mona Al-Harbi",
    gateSet: { key: "capital_gates", version: "1.2", pack: "airports" },
    schedule: {
      state: "slipped",
      why: "the current end date is 81 day(s) later than the baseline of 2026-05-31",
      baselineStart: "2025-09-01",
      baselineEnd: "2026-05-31",
      startDate: "2025-09-14",
      endDate: "2026-08-20",
      endSlipDays: 81,
      startSlipDays: 13,
      overdueDays: 17,
      rebaselined: 1,
      rebaselineNote:
        "the baseline has been moved 1 time(s); slip is measured against the current baseline, not the original",
    },
    money: money(12_600_000_00, 12_600_000_00, 11_940_000_00),
    rebaselines: [
      {
        baselineStart: "2025-09-01",
        baselineEnd: "2026-02-28",
        replacedAt: d(-210),
        replacedBy: "Mona Al-Harbi",
        reason: "Screening equipment delivery slipped by three months after the supplier's factory acceptance failed.",
      },
    ],
    links: [
      {
        entityKind: "work_order",
        entityId: "wo-bhs-3",
        relation: "affects",
        entityRef: "WO-000009",
        entityName: "Carousel 3 drive alignment",
      },
    ],
    createdAt: d(-380),
    updatedAt: d(-11),
    gateSeeds: [
      {
        key: "concept",
        status: "approved",
        supplied: ["business_case", "safety_screening"],
        decidedAt: d(-370),
        decidedByName: "Mona Al-Harbi",
      },
      {
        key: "design",
        status: "approved",
        supplied: ["design_certificate", "design_review_minutes"],
        decidedAt: d(-340),
        decidedByName: "Mona Al-Harbi",
      },
      {
        key: "tender",
        status: "approved",
        supplied: ["tender_report"],
        decidedAt: d(-320),
        decidedByName: "Faisal Al-Ghamdi",
      },
      {
        key: "construction_start",
        status: "approved",
        supplied: ["method_statement", "safety_case", "permit_to_work"],
        decidedAt: d(-300),
        decidedByName: "Mona Al-Harbi",
      },
      {
        /* A waiver, with the reason the platform refuses to accept one without. */
        key: "handover",
        status: "waived",
        supplied: ["as_built_drawings"],
        decidedAt: d(-9),
        decidedByName: "Faisal Al-Ghamdi",
        decisionNote:
          "Handover accepted into operational use ahead of the O&M manuals, which the contractor owes by 30 September. Tracked as a contractual outstanding, not as an open gate.",
      },
    ],
  },
  {
    id: "prj-0005",
    code: "PRJ-2026-0005",
    name: "Perimeter fence and patrol road renewal",
    description:
      "Renewal of 6.2 km of perimeter fence to the current security standard, with the patrol road resurfaced.",
    category: "landside",
    domain: "landside",
    status: "construction",
    siteId: SITE,
    siteCode: "OEMA",
    contractor: "Saudi Services for Electro-Mechanic Works",
    ownerName: "Faisal Al-Ghamdi",
    gateSet: { key: "capital_gates", version: "1.2", pack: "airports" },
    schedule: {
      state: "on_baseline",
      why: "the current end date still matches the baseline of 2026-12-15",
      baselineStart: "2026-07-01",
      baselineEnd: "2026-12-15",
      startDate: "2026-07-05",
      endDate: "2026-12-15",
      endSlipDays: 0,
      startSlipDays: 4,
      rebaselined: 0,
    },
    money: money(4_800_000_00, 4_650_000_00, 900_000_00),
    rebaselines: [],
    links: [],
    createdAt: d(-95),
    updatedAt: d(-30),
    gateSeeds: [
      {
        key: "concept",
        status: "approved",
        supplied: ["business_case", "safety_screening"],
        decidedAt: d(-90),
        decidedByName: "Mona Al-Harbi",
      },
      {
        key: "design",
        status: "approved",
        supplied: ["design_certificate", "design_review_minutes"],
        decidedAt: d(-75),
        decidedByName: "Mona Al-Harbi",
      },
      {
        key: "tender",
        status: "approved",
        supplied: ["tender_report"],
        decidedAt: d(-66),
        decidedByName: "Faisal Al-Ghamdi",
      },
      {
        key: "construction_start",
        status: "approved",
        supplied: ["method_statement", "safety_case", "permit_to_work"],
        decidedAt: d(-63),
        decidedByName: "Mona Al-Harbi",
      },
      { key: "handover", status: "pending" },
    ],
  },
  {
    id: "prj-0006",
    code: "PRJ-2026-0006",
    name: "Apron 3 resurfacing",
    description: "Resurfacing of stands 31–36 with a heavy-duty asphalt to take Code F loading.",
    category: "airfield_pavement",
    domain: "airside",
    status: "complete",
    siteId: SITE,
    siteCode: "OEMA",
    contractor: "Al-Rajhi Contracting",
    ownerName: "Mona Al-Harbi",
    gateSet: { key: "capital_gates", version: "1.2", pack: "airports" },
    schedule: {
      state: "ahead",
      why: "the current end date is 12 day(s) earlier than the baseline of 2026-08-01",
      baselineStart: "2026-01-15",
      baselineEnd: "2026-08-01",
      startDate: "2026-01-15",
      endDate: "2026-07-20",
      endSlipDays: -12,
      startSlipDays: 0,
      rebaselined: 0,
    },
    money: money(6_200_000_00, 6_050_000_00, 6_050_000_00),
    rebaselines: [],
    links: [
      { entityKind: "zone", entityId: "z-apron-3", relation: "affects", entityRef: "APRON-3", entityName: "Apron 3" },
    ],
    createdAt: d(-260),
    updatedAt: d(-44),
    gateSeeds: [
      {
        key: "concept",
        status: "approved",
        supplied: ["business_case", "safety_screening"],
        decidedAt: d(-250),
        decidedByName: "Mona Al-Harbi",
      },
      {
        key: "design",
        status: "approved",
        supplied: ["design_certificate", "design_review_minutes"],
        decidedAt: d(-230),
        decidedByName: "Mona Al-Harbi",
      },
      {
        key: "tender",
        status: "approved",
        supplied: ["tender_report"],
        decidedAt: d(-215),
        decidedByName: "Faisal Al-Ghamdi",
      },
      {
        key: "construction_start",
        status: "approved",
        supplied: ["method_statement", "safety_case", "permit_to_work"],
        decidedAt: d(-205),
        decidedByName: "Mona Al-Harbi",
      },
      {
        key: "handover",
        status: "approved",
        supplied: ["as_built_drawings", "o_and_m_manuals"],
        decidedAt: d(-42),
        decidedByName: "Mona Al-Harbi",
      },
    ],
  },
  {
    id: "prj-0007",
    code: "PRJ-2026-0007",
    name: "Stormwater culvert repair — north airside",
    description:
      "Repair of culvert C-12 under the northern service road, including invert reprofiling and new headwalls.",
    category: "utilities",
    domain: "estate",
    status: "construction",
    siteId: SITE,
    siteCode: "OEMA",
    contractor: "Nesma & Partners",
    ownerName: "Faisal Al-Ghamdi",
    gateSet: { key: "capital_gates", version: "1.2", pack: "airports" },
    schedule: {
      state: "no_current_date",
      why: "this project is baselined but has no current end date, so there is nothing to compare the baseline against",
      baselineStart: "2026-08-15",
      baselineEnd: "2026-10-31",
      startDate: "2026-08-18",
      rebaselined: 0,
    },
    money: money(760_000_00, 760_000_00, 210_000_00),
    rebaselines: [],
    links: [],
    createdAt: d(-25),
    updatedAt: d(-3),
    gateSeeds: [
      {
        key: "concept",
        status: "approved",
        supplied: ["business_case", "safety_screening"],
        decidedAt: d(-22),
        decidedByName: "Mona Al-Harbi",
      },
      {
        key: "design",
        status: "approved",
        supplied: ["design_certificate", "design_review_minutes"],
        decidedAt: d(-18),
        decidedByName: "Mona Al-Harbi",
      },
      {
        key: "tender",
        status: "waived",
        supplied: [],
        decidedAt: d(-16),
        decidedByName: "Faisal Al-Ghamdi",
        decisionNote: "Emergency repair issued against the standing civils framework.",
      },
      {
        key: "construction_start",
        status: "approved",
        supplied: ["method_statement", "safety_case", "permit_to_work"],
        decidedAt: d(-14),
        decidedByName: "Mona Al-Harbi",
      },
      { key: "handover", status: "pending" },
    ],
  },
];

/* ---- derived state, recomputed after every write --------------------------------- */

/** A listing row for a sheet — the shape `inspections.listing` answers. */
function rowOf(s: ProjectInspectionDetail): ProjectInspectionRow {
  const stoppingWork = s.kind === "hold" && !s.releasedAt;
  return {
    id: s.id,
    reference: s.reference,
    title: s.title,
    kind: s.kind,
    discipline: s.discipline,
    checklistKey: s.checklistKey,
    location: s.location,
    scheduledFor: s.scheduledFor,
    performedAt: s.performedAt,
    performedByName: s.performedByName,
    result: s.result,
    releasedAt: s.releasedAt,
    releasedByName: s.releasedByName,
    witnessedBy: s.witnessedBy,
    items: s.quality.items,
    notChecked: s.quality.notChecked,
    failed: s.quality.failed,
    defects: s.defects,
    stoppingWork,
    holdingBecause: stoppingWork ? holdingBecause(s.result, s.quality.notChecked, s.quality.items) : undefined,
    createdAt: s.createdAt,
  };
}

/**
 * Everything a project reports about itself that is counted rather than
 * stored. Called on build and again after every write, so a released hold
 * point stops being reported as stopping the work in the same breath.
 */
export function refresh(p: ProjectDetail): void {
  const mine = SHEETS.filter((s) => s.projectId === p.id);
  p.inspections = mine.map(rowOf);
  p.holdPoints = mine.filter((s) => s.kind === "hold" && !s.releasedAt).map((s) => holdPointOf(s, p));
  p.holding = p.holdPoints.length;
  p.workStopped = p.holding > 0;
  p.gates = p.gateList.length;
  p.gatesApproved = p.gateList.filter((g) => g.status === "approved").length;
  const undecided = p.gateList.filter((g) => !["approved", "rejected", "waived"].includes(g.status));
  p.gateSummary = {
    count: p.gateList.length,
    decided: p.gateList.length - undecided.length,
    nextGate: undecided[0]?.key ?? null,
    decidedWithoutEvidence: p.gateList.filter((g) => g.evidence.state === "decided_without_evidence").map((g) => g.key),
    state: p.gateList.length === 0 ? "no_gates" : undecided.length ? "in_progress" : "all_decided",
    why: p.gateList.length
      ? undefined
      : "this project has no gates at all, which is not the same as having passed them",
  };
}

function holdPointOf(s: ProjectInspectionDetail, p: ProjectDetail) {
  return {
    id: s.id,
    reference: s.reference,
    title: s.title,
    discipline: s.discipline,
    location: s.location,
    kind: s.kind,
    result: s.result,
    scheduledFor: s.scheduledFor,
    performedAt: s.performedAt,
    projectId: p.id,
    projectCode: p.code,
    projectName: p.name,
    projectStatus: p.status,
    siteId: p.siteId,
    siteCode: p.siteCode,
    items: s.quality.items,
    notChecked: s.quality.notChecked,
    failed: s.quality.failed,
    holdingBecause: holdingBecause(s.result, s.quality.notChecked, s.quality.items),
    awaitingReleaseOnly: s.result != null && !BLOCKS_RELEASE.includes(s.result) && s.quality.notChecked === 0,
    createdAt: s.createdAt,
  };
}

export const PROJECTS: ProjectDetail[] = SEEDS.map((seed) => {
  const { gateSeeds, ...rest } = seed;
  const p: ProjectDetail = {
    ...rest,
    gateList: gatesOf(seed.id, gateSeeds),
    gates: 0,
    gatesApproved: 0,
    holding: 0,
    workStopped: false,
    inspections: [],
    holdPoints: [],
    gateSummary: { count: 0, decided: 0, nextGate: null, decidedWithoutEvidence: [], state: "no_gates" },
  };
  refresh(p);
  return p;
});

export function projectRows(siteId: string): ProjectRow[] {
  return PROJECTS.filter((p) => p.siteId === siteId).map((p) => ({
    id: p.id,
    code: p.code,
    name: p.name,
    category: p.category,
    domain: p.domain,
    status: p.status,
    siteId: p.siteId,
    siteCode: p.siteCode,
    ownerId: p.ownerId,
    contractor: p.contractor,
    schedule: p.schedule,
    money: p.money,
    gateSet: p.gateSet,
    gates: p.gates,
    gatesApproved: p.gatesApproved,
    holding: p.holding,
    workStopped: p.workStopped,
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
  }));
}

export function holdPointReport(siteId: string): HoldPointReport {
  const rows = PROJECTS.filter((p) => p.siteId === siteId).flatMap((p) => p.holdPoints);
  return {
    holdPoints: rows,
    count: rows.length,
    holdPointsEver: SHEETS.filter((s) => s.kind === "hold").length,
    state: rows.length ? "work_stopped" : "nothing_holding",
    note: rows.length
      ? "Each of these stops the work it covers until somebody with the authority releases it. A recorded result is not a release."
      : "No hold point is currently unreleased. This is a statement about hold points only; it says nothing about whether the work is otherwise able to proceed.",
  };
}

/**
 * `attention.report`, computed from the same records the rest of the screen
 * reads rather than written out beside them — a sample whose summary and rows
 * can disagree teaches a screen bug to look like sample data.
 */
export function attentionReport(siteId: string): AttentionReport {
  const mine = PROJECTS.filter((p) => p.siteId === siteId);
  const live = mine.filter((p) => LIVE_STATUSES.includes(p.status));
  const todayIso = day(0);

  const lists = {} as Record<AttentionKind, AttentionRow[]>;
  lists.work_stopped = mine.flatMap((p) => p.holdPoints.map((h) => ({ ...h, projectId: p.id })));
  lists.gates_awaiting_decision = mine.flatMap((p) =>
    p.gateList
      .filter((g) => g.status === "submitted")
      .map((g) => ({
        projectId: p.id,
        projectCode: p.code,
        projectName: p.name,
        key: g.key,
        name: g.name,
        seq: g.seq,
        submittedAt: g.submittedAt,
        dueDate: g.dueDate,
        daysWaiting: g.submittedAt ? daysBetween(g.submittedAt, todayIso) : undefined,
        overdue: g.dueDate ? g.dueDate < todayIso : false,
      })),
  );
  lists.gates_decided_without_evidence = mine.flatMap((p) =>
    p.gateList
      .filter((g) => g.evidence.state === "decided_without_evidence")
      .map((g) => ({
        projectId: p.id,
        projectCode: p.code,
        projectName: p.name,
        key: g.key,
        name: g.name,
        seq: g.seq,
        status: p.status,
      })),
  );
  lists.sheets_left_incomplete = mine.flatMap((p) =>
    p.inspections
      .filter((i) => (i.result != null || i.performedAt != null) && i.notChecked > 0)
      .map((i) => ({
        projectId: p.id,
        projectCode: p.code,
        inspectionId: i.id,
        reference: i.reference,
        title: i.title,
        kind: i.kind,
        discipline: i.discipline,
        result: i.result,
        performedAt: i.performedAt,
        notChecked: i.notChecked,
        items: i.items,
      })),
  );
  lists.overdue = live
    .filter((p) => p.schedule.endDate != null && p.schedule.endDate < todayIso)
    .map((p) => scheduleRow(p, { daysOverdue: daysBetween(p.schedule.endDate as string, todayIso) }));
  lists.slipped_against_baseline = live.filter((p) => p.schedule.state === "slipped").map((p) => scheduleRow(p, {}));
  lists.no_baseline = live.filter((p) => p.schedule.baselineEnd == null).map((p) => scheduleRow(p, {}));
  lists.never_inspected = mine
    .filter((p) => (p.status === "construction" || p.status === "commissioning") && p.inspections.length === 0)
    .map((p) => scheduleRow(p, {}));
  lists.rebaselined = mine.filter((p) => p.schedule.rebaselined > 0).map((p) => scheduleRow(p, {}));

  const counts = {} as Record<AttentionKind, number>;
  for (const kind of ATTENTION_KINDS) counts[kind] = lists[kind].length;

  return {
    asOf: todayIso,
    siteId,
    lists,
    counts,
    note: "Each list is a different failure with a different fix. 'no_baseline' is not an on-time project and 'never_inspected' is not a project with nothing wrong; both are projects that no other figure here can describe.",
  };
}

function scheduleRow(p: ProjectDetail, extra: Partial<AttentionRow>): AttentionRow {
  return {
    projectId: p.id,
    projectCode: p.code,
    name: p.name,
    projectName: p.name,
    status: p.status,
    endDate: p.schedule.endDate,
    baselineEnd: p.schedule.baselineEnd,
    startDate: p.schedule.startDate,
    endSlipDays: p.schedule.endSlipDays,
    rebaselined: p.schedule.rebaselined,
    ...extra,
  };
}

function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((Date.parse(toIso) - Date.parse(fromIso)) / 86_400_000);
}
